import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

interface PlanData {
  id: string;
  name: string;
  price: string;
  priceId: string;
  contracts: string;
  description: string;
  features: string[];
}

const PLANS_DATA: Record<string, PlanData> = {
  'teste': {
    id: 'teste',
    name: 'Plano Teste',
    price: '1,00',
    priceId: 'price_1SGPUC52ruC49gBcUU0AuTR9',
    contracts: '5',
    description: 'Plano de teste - Experimente a plataforma',
    features: ['Até 5 contratos por mês', 'Acesso completo às funcionalidades', 'Ideal para testar o sistema']
  },
  'essencial-mensal': {
    id: 'essencial-mensal',
    name: 'Essencial Mensal',
    price: '189,90',
    priceId: 'price_1S6ZmQ0K3IglKFvrMOTbDzcq',
    contracts: '5',
    description: 'Ideal para pequenas empresas',
    features: ['Até 5 contratos por mês', 'Suporte básico', 'Integrações essenciais']
  },
  'profissional-mensal': {
    id: 'profissional-mensal',
    name: 'Profissional Mensal',
    price: '576,80',
    priceId: 'price_1S6Zov0K3IglKFvrg9DPYw2f',
    contracts: '10',
    description: 'Para empresas em crescimento',
    features: ['Até 10 contratos por mês', 'Suporte prioritário', 'Todas as integrações']
  },
  'corporativo-mensal': {
    id: 'corporativo-mensal',
    name: 'Corporativo Mensal',
    price: '969,90',
    priceId: 'price_1S6Zpi0K3IglKFvr2YYqwgr1',
    contracts: '30',
    description: 'Para grandes empresas',
    features: ['Até 30 contratos por mês', 'Suporte dedicado', 'Customizações avançadas']
  },
  'essencial-anual': {
    id: 'essencial-anual',
    name: 'Essencial Anual',
    price: '161,42',
    priceId: 'price_1S7ck90K3IglKFvrGRK1VybZ',
    contracts: '5',
    description: 'Ideal para pequenas empresas - Plano Anual',
    features: ['Até 5 contratos por mês', '15% de desconto', 'Suporte básico']
  },
  'profissional-anual': {
    id: 'profissional-anual',
    name: 'Profissional Anual',
    price: '490,28',
    priceId: 'price_1S7cjX0K3IglKFvrF6gqNAKw',
    contracts: '10',
    description: 'Para empresas em crescimento - Plano Anual',
    features: ['Até 10 contratos por mês', '15% de desconto', 'Suporte prioritário']
  },
  'corporativo-anual': {
    id: 'corporativo-anual',
    name: 'Corporativo Anual',
    price: '824,29',
    priceId: 'price_1S7cin0K3IglKFvrGyz2w6Ys',
    contracts: '30',
    description: 'Para grandes empresas - Plano Anual',
    features: ['Até 30 contratos por mês', '15% de desconto', 'Suporte dedicado']
  }
};

export default function PlanCheckout() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const plan = planId ? PLANS_DATA[planId] : null;

  if (!plan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <p className="text-muted-foreground mb-4">Plano não encontrado</p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos.",
        variant: "destructive"
      });
      return;
    }

    if (!acceptedTerms) {
      toast({
        title: "Aceite necessário",
        description: "Você deve aceitar os termos do contrato para continuar.",
        variant: "destructive"
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Senhas não coincidem",
        description: "A senha e confirmação devem ser iguais.",
        variant: "destructive"
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter pelo menos 6 caracteres.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-checkout-with-account', {
        body: {
          priceId: plan.priceId,
          email,
          password,
          planData: {
            planId: plan.id,
            planName: plan.name,
            planPrice: plan.price
          }
        }
      });

      if (error) throw error;

      // Abrir checkout do Stripe em nova aba
      if (data.url) {
        window.open(data.url, '_blank');
      }

    } catch (error: any) {
      console.error('Erro ao criar checkout:', error);
      toast({
        title: "Erro no checkout",
        description: error.message || "Erro ao processar pagamento",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getContractText = (plan: PlanData) => {
    const isAnnual = plan.id.includes('anual');
    const period = isAnnual ? 'anual' : 'mensal';
    const periodText = isAnnual ? 'mensais' : 'mensais';
    const cancelNotice = isAnnual ? '30 dias' : '15 dias';
    
    return `CONTRATO DE PRESTAÇÃO DE SERVIÇOS

Entre as partes a seguir qualificadas, celebra-se o presente contrato:

CONTRATADO: Lyra RH Tecnologia Ltda., inscrita no CNPJ sob nº 31.008.646/0001-49, com sede em R. Dona Elisa, 33, casa 2 - Barra Funda, São Paulo - SP, 01155-030, e-mail contato@lyrarh.com.br e WhatsApp (11) 99417-0756.

1. Definições

Para fins deste contrato, entende-se por: (i) "Plataforma" o sistema Lyra RH; (ii) "Contratação" o processo completo de admissão de colaborador; (iii) "Dados Pessoais" conforme definido na LGPD; (iv) "SLA" os prazos de atendimento descritos neste instrumento.

2. Objeto

O presente contrato tem por objeto a disponibilização da Plataforma Lyra RH com direito a até ${plan.contracts} contratações ${periodText}, incluindo os seguintes recursos: editor de propostas e contratos, validação automática de documentos por inteligência artificial, leitura obrigatória de informações e jornada integrada via WhatsApp.

3. Serviços Inclusos

O CONTRATADO fornecerá acesso à Plataforma, atualizações evolutivas e suporte técnico padrão no horário comercial (Segunda a Sexta-feira, das 9h às 18h – horário de Brasília), por e-mail e WhatsApp.

4. Limites e Excedentes

O uso da Plataforma está limitado ao número de contratações previsto neste contrato. Ultrapassado esse limite, será cobrado o valor de excedentes conforme tabela vigente do Lyra RH. O CONTRATADO poderá adotar mecanismos técnicos para restringir uso abusivo.

5. Preço e Pagamento

O valor do plano é de R$ ${plan.price} por mês, com cobrança ${period} recorrente via Stripe. Tributos incidentes serão acrescidos ao preço. O atraso no pagamento autoriza a suspensão dos serviços até a regularização.

6. Reajuste

Os valores serão reajustados anualmente pelo índice IPCA acumulado (ou outro que o substitua), após 12 meses de vigência, mediante comunicação prévia.

7. Vigência e Renovação

O contrato terá vigência ${period}, renovando-se automaticamente por iguais períodos, salvo manifestação contrária de qualquer das partes com antecedência mínima de ${cancelNotice}.

8. Cancelamento e Rescisão

Qualquer das partes poderá rescindir este contrato mediante aviso prévio de ${cancelNotice}. Inadimplência superior a 10 dias autoriza a suspensão imediata dos serviços e rescisão posterior.

9. Suporte e SLA

O CONTRATADO atenderá as solicitações de suporte conforme prioridades: P1 (indisponibilidade total) – resposta até 4h úteis; P2 (falhas críticas) – até 8h úteis; P3 (dúvidas gerais) – até 24h úteis.

10. Manutenção e Disponibilidade

Manutenções programadas serão informadas com antecedência mínima de 24h. O CONTRATADO buscará alta disponibilidade, sem garantia absoluta de uptime.

11. LGPD e Privacidade

O CONTRATANTE é o Controlador dos dados pessoais tratados; o CONTRATADO é o Operador, comprometendo-se a adotar medidas técnicas e administrativas adequadas para proteção dos dados.

12. Segurança da Informação

O CONTRATADO empregará criptografia em trânsito, controle de acesso e registros de auditoria. O CONTRATANTE manterá credenciais em sigilo e gestão de perfis de acesso.

13. Obrigações do CONTRATANTE

Fornecer informações verdadeiras e atualizadas; respeitar limites de uso; manter pagamentos em dia; não burlar mecanismos de segurança.

14. Obrigações do CONTRATADO

Manter a Plataforma disponível; corrigir falhas; prover suporte técnico conforme SLA; comunicar manutenções programadas; tratar dados pessoais conforme LGPD.

15. Propriedade Intelectual

O software, marcas e materiais do CONTRATADO são protegidos por direitos de propriedade intelectual. Não há cessão de código-fonte. O CONTRATANTE permanece titular dos dados inseridos na Plataforma.

16. Confidencialidade

As partes se obrigam a manter sigilo sobre informações confidenciais obtidas em razão deste contrato, salvo obrigação legal de divulgação.

17. Limitação de Responsabilidade

A responsabilidade do CONTRATADO fica limitada ao total pago pelo CONTRATANTE nos 3 meses anteriores ao evento. Excluem-se danos indiretos, lucros cessantes e perdas de receita.

18. Força Maior

Fatos fora do controle razoável das partes eximem responsabilidade enquanto perdurarem.

19. Alterações de Plano

O CONTRATANTE poderá solicitar upgrade imediato com cobrança proporcional. Downgrade terá efeito apenas na renovação.

20. Comunicações

As comunicações ocorrerão pelos e-mails e WhatsApp cadastrados. Alterações contratuais serão comunicadas com aviso prévio de 15 dias.

21. Disposições Gerais

Este contrato não gera vínculo trabalhista. Nulidade parcial não invalida o restante. Cessão de direitos exige anuência prévia da outra parte.

22. Foro

Fica eleito o foro da comarca da sede do CONTRATADO, com renúncia expressa a qualquer outro, por mais privilegiado que seja.`;
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        <Button 
          onClick={() => navigate('/')} 
          variant="ghost" 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar aos Planos
        </Button>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Resumo do Plano */}
          <Card>
            <CardHeader>
              <CardTitle>Resumo do Plano</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{plan.name}</h3>
                <p className="text-muted-foreground text-sm">{plan.description}</p>
              </div>
              
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">R$ {plan.price}</span>
                <span className="text-muted-foreground">
                  {plan.id.includes('anual') ? '/mês (anual)' : '/mês'}
                </span>
              </div>

              <div className="space-y-2">
                <p className="font-medium">Incluso:</p>
                <ul className="space-y-1">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1.5">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Formulário de Checkout */}
          <Card>
            <CardHeader>
              <CardTitle>Dados da Conta</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCheckout} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirme sua senha"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                 </div>

                 <div className="space-y-3">
                   <div className="flex items-start space-x-3">
                     <Checkbox 
                       id="terms-accept"
                       checked={acceptedTerms}
                       onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                     />
                     <div className="text-sm leading-relaxed">
                       <label htmlFor="terms-accept" className="cursor-pointer">
                         Li e aceito os{' '}
                         <Dialog>
                           <DialogTrigger asChild>
                             <button type="button" className="text-primary underline hover:text-primary/80">
                               Termos do contrato
                             </button>
                           </DialogTrigger>
                           <DialogContent className="max-w-4xl max-h-[80vh]">
                             <DialogHeader>
                               <DialogTitle>
                                 Contrato – {plan.name} ({plan.contracts} contratações por R$ {plan.price}/mês)
                               </DialogTitle>
                             </DialogHeader>
                             <ScrollArea className="h-96 w-full rounded border p-4">
                               <div className="text-sm leading-relaxed whitespace-pre-line">
                                 {getContractText(plan)}
                               </div>
                             </ScrollArea>
                           </DialogContent>
                         </Dialog>
                       </label>
                     </div>
                   </div>
                 </div>

                 <Button 
                   type="submit" 
                   className="w-full" 
                   disabled={loading || !acceptedTerms}
                 >
                   {loading ? "Processando..." : "Continuar para Pagamento"}
                 </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Ao continuar, você será redirecionado para o Stripe para processar o pagamento de forma segura.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}